/**
 * 批量任务服务模块：处理批量任务的提交和状态查询
 */

import { apiService } from './api'
import type {
  BatchTaskSubmitParams,
  BatchTaskSubmitResponse,
  BatchTaskStatusResponse,
  BatchTask,
  BatchTaskListResponse,
  TaskApiResponse,
  BatchTaskListParams,
  BatchTaskResultsResponse,
  RetryTasksParams,
  RetryTasksResponse,
  DownloadBatchParams,
  DownloadBatchResponse,
} from '@/types/task'

const BATCH_TASK_ENDPOINT = '/api/user/batch-tasks' as const

interface BatchTaskActionResponse {
  message?: string
  cancelled_count?: number
  completed_count?: number
}

export interface BatchTaskImageURLsResponse {
  batch_task_id: string
  status: string
  total_count: number
  success_count: number
  failed_count: number
  image_urls: {
    index: number
    input_url: string
    output_url: string
  }[]
}

/**
 * 提交批量任务
 * @param params 提交参数
 * @returns 提交结果
 */
export async function submitBatchTask(
  params: BatchTaskSubmitParams
): Promise<BatchTaskSubmitResponse> {
  const response = await apiService.post<
    TaskApiResponse<BatchTaskSubmitResponse>
  >(BATCH_TASK_ENDPOINT, params)
  return response.data
}

/**
 * 获取批量任务状态
 * @param batchId 批量任务 ID
 * @returns 任务状态
 */
export async function getBatchTaskStatus(
  batchId: string
): Promise<BatchTaskStatusResponse> {
  const response = await apiService.get<
    TaskApiResponse<BatchTaskStatusResponse>
  >(`${BATCH_TASK_ENDPOINT}/${batchId}/status`)
  return response.data
}

/**
 * 获取批量任务详情
 * @param batchId 批量任务 ID
 * @returns 任务详情
 */
export async function getBatchTask(batchId: string): Promise<BatchTask> {
  const response = await apiService.get<TaskApiResponse<BatchTask>>(
    `${BATCH_TASK_ENDPOINT}/${batchId}`
  )
  return response.data
}

/**
 * 获取批量任务列表
 * @param page 页码
 * @param pageSize 每页数量
 * @returns 任务列表
 */
export async function listBatchTasks(
  page = 1,
  pageSize = 10
): Promise<BatchTaskListResponse> {
  const response = await apiService.get<TaskApiResponse<BatchTaskListResponse>>(
    BATCH_TASK_ENDPOINT,
    { page: String(page), pageSize: String(pageSize) }
  )
  return response.data
}

/**
 * 获取工作流的批量任务历史记录
 * @param workflowId 工作流 ID
 * @param page 页码
 * @param pageSize 每页数量
 * @returns 批量任务列表
 */
export async function getWorkflowBatchTasks(
  workflowId: string,
  page = 1,
  pageSize = 10
): Promise<BatchTaskListResponse> {
  const response = await apiService.get<TaskApiResponse<BatchTaskListResponse>>(
    BATCH_TASK_ENDPOINT,
    {
      workflow_id: workflowId,
      page: String(page),
      pageSize: String(pageSize),
    }
  )
  return response.data
}

/**
 * 取消批量任务
 * @param batchId 批量任务 ID
 */
export async function cancelBatchTask(
  batchId: string
): Promise<BatchTaskActionResponse> {
  const response = await apiService.post<TaskApiResponse<BatchTaskActionResponse>>(
    `${BATCH_TASK_ENDPOINT}/${batchId}/cancel`
  )
  return response.data
}

/**
 * 暂停批量任务
 * @param batchId 批量任务 ID
 */
export async function pauseBatchTask(
  batchId: string
): Promise<BatchTaskActionResponse> {
  const response = await apiService.post<TaskApiResponse<BatchTaskActionResponse>>(
    `${BATCH_TASK_ENDPOINT}/${batchId}/pause`
  )
  return response.data
}

/**
 * 恢复批量任务
 * @param batchId 批量任务 ID
 */
export async function resumeBatchTask(
  batchId: string
): Promise<BatchTaskActionResponse> {
  const response = await apiService.post<TaskApiResponse<BatchTaskActionResponse>>(
    `${BATCH_TASK_ENDPOINT}/${batchId}/resume`
  )
  return response.data
}

/**
 * 获取全部批量任务列表（支持备注搜索）
 * @param params 查询参数
 * @returns 批量任务列表
 */
export async function listAllBatchTasks(
  params: BatchTaskListParams = {}
): Promise<BatchTaskListResponse> {
  const queryParams: Record<string, string> = {}

  if (params.page !== undefined) {
    queryParams['page'] = String(params.page)
  }
  if (params.page_size !== undefined) {
    queryParams['pageSize'] = String(params.page_size)
  }
  if (params.remark !== undefined && params.remark !== '') {
    queryParams['remark'] = params.remark
  }
  if (params.workflow_id !== undefined && params.workflow_id !== '') {
    queryParams['workflow_id'] = params.workflow_id
  }

  const response = await apiService.get<TaskApiResponse<BatchTaskListResponse>>(
    BATCH_TASK_ENDPOINT,
    queryParams
  )
  return response.data
}

/**
 * 获取批量任务详情（含子任务结果分页）
 * @param batchId 批量任务 ID
 * @param page 页码
 * @param pageSize 每页数量
 * @returns 批量任务详情和子任务结果
 */
export async function getBatchTaskResults(
  batchId: string,
  page = 1,
  pageSize = 20
): Promise<BatchTaskResultsResponse> {
  const response = await apiService.get<TaskApiResponse<BatchTaskResultsResponse>>(
    `${BATCH_TASK_ENDPOINT}/${batchId}/results`,
    {
      page: String(page),
      page_size: String(pageSize),
    }
  )
  return response.data
}

/**
 * 获取批量任务成功图片链接
 * @param batchId 批量任务 ID
 * @returns 图片链接列表
 */
export async function getBatchTaskImageURLs(
  batchId: string
): Promise<BatchTaskImageURLsResponse> {
  const response = await apiService.get<TaskApiResponse<BatchTaskImageURLsResponse>>(
    `${BATCH_TASK_ENDPOINT}/${batchId}/image-urls`
  )
  return response.data
}

/**
 * 重试失败的子任务
 * @param batchId 批量任务 ID
 * @param params 重试参数
 * @returns 重试结果
 */
export async function retryFailedTasks(
  batchId: string,
  params: RetryTasksParams
): Promise<RetryTasksResponse> {
  const response = await apiService.post<TaskApiResponse<RetryTasksResponse>>(
    `${BATCH_TASK_ENDPOINT}/${batchId}/retry`,
    params
  )
  return response.data
}

/**
 * 批量下载任务结果
 * @param batchId 批量任务 ID
 * @param params 下载参数
 * @returns 下载链接信息
 */
export async function downloadBatchResults(
  batchId: string,
  params: DownloadBatchParams
): Promise<DownloadBatchResponse> {
  const response = await apiService.post<TaskApiResponse<DownloadBatchResponse>>(
    `${BATCH_TASK_ENDPOINT}/${batchId}/download`,
    params
  )
  return response.data
}
